import React, { useMemo } from 'react';

const DonutChart = React.memo(({
    segments = [],
    size = 226,
    strokeWidth = 26,
    gapDegrees = 2.5,
    fluid = false,
    children,
}) => {
    const radius = (size - strokeWidth) / 2;
    const circumference = 2 * Math.PI * radius;
    const validSegments = useMemo(() => {
        return segments.filter((segment) => (segment.value || 0) > 0);
    }, [segments]);
    const total = validSegments.reduce((sum, segment) => sum + (segment.value || 0), 0) || 1;
    const effectiveGapLength = validSegments.length <= 1 ? 0 : (gapDegrees / 360) * circumference;

    const arcs = useMemo(() => {
        let cumulative = 0;
        return validSegments.map((segment) => {
            const fraction = (segment.value || 0) / total;
            const segmentLength = Math.max(fraction * circumference - effectiveGapLength, 0);
            const arc = {
                ...segment,
                strokeDasharray: `${segmentLength} ${Math.max(circumference - segmentLength, 0)}`,
                strokeDashoffset: -cumulative * circumference,
            };
            cumulative += fraction;
            return arc;
        });
    }, [validSegments, total, circumference, effectiveGapLength]);

    return (
        <div style={fluid
            ? { position: "relative", width: "100%", maxWidth: size, aspectRatio: "1 / 1", minWidth: 0 }
            : { position: "relative", width: size, height: size, flexShrink: 0 }
        }>
            <svg
                width={fluid ? "100%" : size}
                height={fluid ? "100%" : size}
                viewBox={`0 0 ${size} ${size}`}
                style={{ overflow: "visible", display: fluid ? "block" : undefined }}
            >
                <circle
                    cx={size / 2}
                    cy={size / 2}
                    r={radius}
                    fill="none"
                    stroke="#FFFFFF"
                    strokeWidth={strokeWidth}
                />
                <g transform={`rotate(-90 ${size / 2} ${size / 2})`}>
                    {arcs.map((arc, index) => (
                        <circle
                            key={index}
                            cx={size / 2}
                            cy={size / 2}
                            r={radius}
                            fill="none"
                            stroke={arc.color}
                            strokeWidth={strokeWidth}
                            strokeLinecap="round"
                            strokeDasharray={arc.strokeDasharray}
                            strokeDashoffset={arc.strokeDashoffset}
                        />
                    ))}
                </g>
            </svg>
            <div style={{
                position: "absolute",
                inset: 0,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexDirection: "column",
            }}>
                {children}
            </div>
        </div>
    );
});

DonutChart.displayName = 'DonutChart';

export default DonutChart;
